/**
 * The signed-in user's study access, decided in one place: the server's assignment-derived access
 * combined with the study's state and the user's team roles.
 */

import { _isBlank, _notNil } from '@/littledash';
import type { CurrentUserStudyAccess, Study } from '@/model/Study.model';
import type { StudyUser, StudyUserSummaryV1 } from '@/model/User.model';
import { currentUserSelector, featuresSelector, teamOwnerSelector } from '@/support/Selectors';
import store from '@/store';

/** The parts of a study that access is decided from. A full `Study` satisfies it. */
export type StudyAccessSubject = Pick<Study, 'archived_at' | 'canceled_at' | 'current_user_access'> & {
  owner?: Pick<StudyUser, 'id'> | null;
  author?: Pick<StudyUser, 'id'> | null;
};

/** The parts of a study-users row that access is decided from. */
export type StudyUserAccessRow = Pick<StudyUserSummaryV1, 'id' | 'access'>;

export interface StudyWriteOptions {
  /** Treat a completed study as writable. */
  allowArchived?: boolean;
  /** Treat a cancelled study as writable. */
  allowCancelled?: boolean;
}

const ALLOW_CLOSED: StudyWriteOptions = { allowArchived: true, allowCancelled: true };

const currentUser = () => currentUserSelector(store.getState());

/**
 * The signed-in user's assignment-derived access to the study, or null when they have neither a
 * direct assignment nor a user group granting any, or when no user is signed in.
 */
export const currentUserStudyAccess = (study: StudyAccessSubject | undefined): CurrentUserStudyAccess | null =>
  _notNil(currentUser()) ? (study?.current_user_access ?? null) : null;

export const isStudyOwner = (study: StudyAccessSubject | undefined): boolean => {
  const user = currentUser();
  return _notNil(user) && Number(user.id) === Number(study?.owner?.id);
};

export const isStudyAuthor = (study: StudyAccessSubject | undefined): boolean => {
  const user = currentUser();
  return _notNil(user) && Number(user.id) === Number(study?.author?.id);
};

const isStudyOwnerOrAuthor = (study: StudyAccessSubject | undefined): boolean =>
  isStudyOwner(study) || isStudyAuthor(study);

export const isTeamOwner = (): boolean => teamOwnerSelector(store.getState());

/**
 * A team owner who may open any study's settings — unless the team has disabled admin access to
 * study settings.
 */
export const isTeamOwnerWithStudySettingsAccess = (): boolean =>
  isTeamOwner() && !(featuresSelector(store.getState()).disable_admin_study_settings_access ?? false);

export const hasTeamStudiesReadAll = (): boolean => Boolean(store.getState().team?.team?.studies_read_all);

const isOpenForWrite = (
  study: StudyAccessSubject,
  { allowArchived = false, allowCancelled = false }: StudyWriteOptions
): boolean => (allowArchived || _isBlank(study.archived_at)) && (allowCancelled || _isBlank(study.canceled_at));

/**
 * Whether the signed-in user is on the study in their own right: assigned directly or through a
 * user group, or its owner or author.
 */
export const isAssignedToStudy = (study: StudyAccessSubject | undefined): boolean =>
  _notNil(currentUserStudyAccess(study)) || isStudyOwnerOrAuthor(study);

/** Assigned to the study, a team owner, or on a team where everyone may read every study. */
export const canReadStudy = (study: StudyAccessSubject | undefined): boolean =>
  _notNil(study) && (isAssignedToStudy(study) || isTeamOwner() || hasTeamStudiesReadAll());

/** Write access through an assignment, ownership or authorship, on an open study unless `options` allow otherwise. */
export const canWriteStudy = (study: StudyAccessSubject | undefined, options: StudyWriteOptions = {}): boolean =>
  _notNil(study) &&
  isOpenForWrite(study, options) &&
  (currentUserStudyAccess(study)?.access === 'write' || isStudyOwnerOrAuthor(study));

/**
 * Whether the signed-in user's row in a study-users list (`GET /api/v1/studies/{studyId}/users`)
 * grants write. Each row carries the access that applies after precedence, so a user who reaches
 * the study only through a group has a row too.
 */
export const hasWriteInStudyUsers = (studyUsers: Array<StudyUserAccessRow> | undefined): boolean => {
  const user = currentUser();
  return (studyUsers ?? []).some((studyUser) => studyUser.id === user?.id && studyUser.access === 'write');
};

/**
 * {@link canWriteStudy}, taking assignment-derived access from a study-users list rather than the
 * study, for screens that load the list instead of the full study.
 */
export const canWriteStudyWithUsers = (
  study: StudyAccessSubject | undefined,
  studyUsers: Array<StudyUserAccessRow> | undefined,
  options: StudyWriteOptions = {}
): boolean =>
  _notNil(study) && isOpenForWrite(study, options) && (hasWriteInStudyUsers(studyUsers) || isStudyOwnerOrAuthor(study));

/**
 * Whether the signed-in user can edit the study's settings: write access — completed and cancelled
 * studies included — or a team owner allowed into study settings.
 */
export const canEditStudySettings = (study: StudyAccessSubject | undefined): boolean =>
  canWriteStudy(study, ALLOW_CLOSED) || isTeamOwnerWithStudySettingsAccess();

/**
 * {@link canEditStudySettings} while the study is still open — the people who may change its
 * tasks and alerts.
 */
export const canEditOpenStudySettings = (study: StudyAccessSubject | undefined): boolean =>
  _notNil(study) && isOpenForWrite(study, {}) && canEditStudySettings(study);

/**
 * Whether the signed-in user can change the study's status — complete, cancel, resume or delete
 * it: its author or owner, or a team owner allowed into study settings.
 */
export const canChangeStudyStatus = (study: StudyAccessSubject | undefined): boolean =>
  isStudyOwnerOrAuthor(study) || isTeamOwnerWithStudySettingsAccess();

/**
 * Whether the signed-in user can transfer the study to a new owner: its author, or a team owner
 * allowed into study settings.
 */
export const canChangeStudyOwner = (study: StudyAccessSubject | undefined): boolean =>
  isStudyAuthor(study) || isTeamOwnerWithStudySettingsAccess();

/**
 * Whether the signed-in user may use bulk actions on the study's observations: its owner or
 * author, while the study is open. Write users are deliberately excluded, matching what the
 * observations page has always allowed.
 */
export const canBulkEditObservations = (study: StudyAccessSubject | undefined): boolean =>
  _notNil(study) && isOpenForWrite(study, {}) && isStudyOwnerOrAuthor(study);
